import { useTheme } from '@mui/styles';
// import PcgUsersView from 'Containers/admin/pages/pcg/PCGNumber';
import { keycloakService } from '@tassat/authentication';
import { AdminRolesMap, AuthorizationSuccessResponse } from '@tassat/common-definitions';
import { UserRecord, UserRole } from '@tassat/fred-definitions';
import React, { Suspense } from 'react';
import { Provider } from 'react-redux';
import {
    Redirect, Route, Router, Switch
} from 'react-router-dom';

import Admin from 'Containers/admin/admin';
import AdminUsers from 'Containers/admin/pages/adminUsers';
import Alerts from 'Containers/admin/pages/alerts';
import ApiConnections from 'Containers/admin/pages/api/apiConnectionsView';
import ApiFirmView from 'Containers/admin/pages/api/apiFirmView';
import ClientMessages from 'Containers/admin/pages/clientMessages';
import Collateral from 'Containers/admin/pages/collateral';
import Firms from 'Containers/admin/pages/firms/firms';
import FirmView from 'Containers/admin/pages/firms/firmView';
import AcgUsersView from 'Containers/admin/pages/pcg/PCGNumber';
import AdminTransactionHistory from 'Containers/admin/pages/transactionHistory';
import InvalidUser from 'Containers/common/invalidUser';
import LockedUser from 'Containers/common/lockedUser';
import SSOLogout from 'Containers/common/logout';
import Offline from 'Containers/common/offline';
import PageLoading from 'Containers/common/pageLoading';
import PendingSetup from 'Containers/common/pendingSetup';
import SSOUserRequestCreated from 'Containers/common/ssoUserRequestCreated';
import authService from 'Lib/auth/AuthService';
import InactivityTimer from 'Lib/auth/InactivityTimer';
import { handleAuthentication, handleCodeAuthentication } from 'Lib/authHelper';
import history from 'Lib/browserHistory';
import { RouteChangeTracker } from 'Lib/googleAnalytics/UserTracker';
import { getLoginMethod } from 'Lib/loginMethodFn';
import { getAdminPermissionsByFeatures } from 'Lib/permissionHelper';
import { ApplicationMuiTheme } from 'Lib/themes/muiTheme';
import Worker from 'Lib/worker/PrimusWorker';
import adminStore from 'Reducers/store/adminStore';
import { useAppSelector } from 'Reducers/store/hooks';
import {
    getCurrentRole, getFetchedUserRoles, getUiConnected, getUser
} from 'Selectors/common';
import Logout from 'SharedComponents/auth/Logout';
import { ProtectedAdminRoute } from 'SharedComponents/auth/ProtectedRoute';

const superAdmin = { '9': 'Super Admin' };

interface AdminRouteListProps {
    connectedToCore: boolean;
    fetchedUserRoles: boolean;
    user: UserRecord;
    userRole: UserRole;
}

const AdminLanding = (props: AdminRouteListProps) => {
    const {
        connectedToCore,
        fetchedUserRoles,
        user,
        userRole
    } = props;

    const muiTheme = useTheme<ApplicationMuiTheme>();
    const isFetchingRoles = React.useRef(false);
    const [ noRouteFound, setNoRouteFound ] = React.useState<boolean>(false);

    React.useEffect(() => {
        const fetchRoles = async () => {
            isFetchingRoles.current = true;
            await Worker.getInstance()
                .getAllUserRoles();
            isFetchingRoles.current = false;
        };

        if (
            !isFetchingRoles.current &&
            !fetchedUserRoles &&
            Object.keys(userRole).length === 0 &&
            connectedToCore &&
            user
        ) {
            fetchRoles();
        }
    }, [
        connectedToCore,
        fetchedUserRoles,
        user,
        userRole
    ]);

    React.useEffect(() => {
        if (!muiTheme) return;

        const adminItems = getAdminPermissionsByFeatures(muiTheme);

        const filteredAdminItems = adminItems.filter(i => userRole &&
            i.permittedRoles.includes(AdminRolesMap[userRole.UserRoleType] || superAdmin[userRole.UserRoleType]));

        if (filteredAdminItems.length > 0) {
            history.replace(filteredAdminItems[0] && filteredAdminItems[0].url);
        } else {
            setNoRouteFound(true);
        }
    }, [ muiTheme, userRole ]);

    if (userRole === undefined && fetchedUserRoles) {
    // something is messed up with the user
        console.error('User has invalid roleId');

        return null;
    }

    if (
        fetchedUserRoles &&
        user &&
        (userRole === undefined ||
            (noRouteFound && connectedToCore))
    ) {
        return <p style={{ paddingLeft: '1rem' }}>Your user role is misconfigured. Please contact support.</p>;
    }

    Worker.getInstance()
        .setSocketRole(userRole);

    return null;
};

const AdminRouteList = () => {
    const userRole = useAppSelector(getCurrentRole);
    const fetchedUserRoles = useAppSelector(getFetchedUserRoles);
    const connectedToCore = useAppSelector(getUiConnected);
    const user = useAppSelector(getUser);

    const muiTheme = useTheme<ApplicationMuiTheme>();

    const adminPermissions = React.useMemo(() => getAdminPermissionsByFeatures(muiTheme), [muiTheme]);
    const authSSO = muiTheme?.app.clientTheme.features.Authentication.SSO;

    const validateUserRole = React.useCallback((permissionId: string, userRole: UserRole): boolean => {
        if (!adminPermissions) return false;
        const permissionToCheck = adminPermissions.find(perm => perm.id === permissionId);

        return permissionToCheck?.permission.some(perm => userRole[perm]) ?? false;
    }, [adminPermissions]);

    return (
        <Router history={history}>
            <>
                <InactivityTimer
                    locationPath={history.location?.pathname}
                    logout={() => {
                        Worker.getInstance()
                            .inactivityLogout();
                        authService.logout();
                    }}
                />
                <RouteChangeTracker isAdmin={true} />
                <Switch>
                    {!authSSO &&
                        <Route
                            path='/admin_auth_login'
                            render={() => {
                            getLoginMethod(true, muiTheme);

                            return null;
                        }}
                        />}
                    <Route
                        path='/invalid'
                        render={() => <InvalidUser />}
                    />
                    <Route
                        path='/notFound'
                        render={() => <SSOLogout />}
                    />
                    <Route
                        path='/requestCreated'
                        render={() => <SSOUserRequestCreated />}
                    />
                    <Route
                        path='/pendingSetup'
                        render={() => <PendingSetup />}
                    />
                    <Route
                        path='/ssoLogout'
                        render={() => <SSOLogout />}
                    />
                    <Route
                        path='/backchannelLogout'
                        render={(props) => {
                            keycloakService.backchannelLogout(true);

                            return null;
                        }}
                    />
                    <Route
                        path='/admin_callback'
                        render={(props) => {
                            handleAuthentication(props, true, muiTheme);

                            return null;
                        }}
                    />
                    <Route
                        path='/admin_callback_code'
                        render={(props) => {
                            handleCodeAuthentication(props);

                            return null;
                        }}
                    />
                    {/**Following route handles silent login functionality with any Idp service iff there
                     * is an existing session with an oidc client. */}
                    <Route
                        path='/silent_login'
                        render={() => {
                            keycloakService.handleAuthentication(true, {} as AuthorizationSuccessResponse);

                            return <PageLoading />;
                        }}
                    />
                    <Route
                        component={Logout}
                        path='/logout'
                    />
                    <Route
                        path='/admin'
                        render={() => {
                            return (
                                <Admin>
                                    <Suspense fallback={<PageLoading />}>
                                        <Switch>
                                            {userRole && (validateUserRole('Firms', userRole) || validateUserRole('ViewFirms', userRole)) && <ProtectedAdminRoute
                                                component={FirmView}
                                                path='/admin/firms/:firmId/withdrawals/:accountWithdrawalDataId'
                                            />}
                                            {userRole && (validateUserRole('Firms', userRole) || validateUserRole('ViewFirms', userRole)) && <ProtectedAdminRoute
                                                component={FirmView}
                                                path='/admin/firms/:firmId/users/:userId'
                                            />}
                                            {userRole && (validateUserRole('Firms', userRole) ||
                                                validateUserRole('ViewFirms', userRole)) &&
                                                <ProtectedAdminRoute
                                                    component={FirmView}
                                                    path='/admin/firms/:firmId/bdaccounts/:accountId'
                                                />}
                                            {userRole &&
                                                (validateUserRole('Firms', userRole) || validateUserRole('ViewFirms', userRole)) && <ProtectedAdminRoute
                                                    component={FirmView}
                                                    path='/admin/firms/:firmId/accounts/:accountId'
                                                />}
                                            {userRole && (validateUserRole('Firms', userRole) || validateUserRole('ViewFirms', userRole)) && <ProtectedAdminRoute
                                                component={FirmView}
                                                path='/admin/firms/:firmId'
                                            />}
                                            {userRole && validateUserRole('APIFirms', userRole) && <ProtectedAdminRoute
                                                component={ApiFirmView}
                                                path='/admin/firms/:firmId/api/:apiConnFirmId'
                                            />}
                                            {userRole && validateUserRole('APIFirms', userRole) && <ProtectedAdminRoute
                                                component={ApiFirmView}
                                                path='/admin/firms/:firmId'
                                            />}
                                            {userRole && (validateUserRole('Firms', userRole) || validateUserRole('APIFirms', userRole) || validateUserRole('ViewFirms', userRole)) && <ProtectedAdminRoute
                                                component={Firms}
                                                path='/admin/firms'
                                            />}
                                            {userRole && validateUserRole('AdminUsers', userRole) && <ProtectedAdminRoute
                                                component={AdminUsers}
                                                path='/admin/users/:userId?'
                                            />}
                                            {userRole && validateUserRole('Transactions', userRole) && <ProtectedAdminRoute
                                                component={AdminTransactionHistory}
                                                path='/admin/history/:firmId'
                                            />}
                                            {userRole && validateUserRole('Transactions', userRole) && <ProtectedAdminRoute
                                                component={AdminTransactionHistory}
                                                path='/admin/history'
                                            />}
                                            {userRole && validateUserRole('APIConnections', userRole) && <ProtectedAdminRoute
                                                component={ApiConnections}
                                                path='/admin/apiConnections/:apiConnectionId?'
                                            />}
                                            {userRole && validateUserRole('APIConnections', userRole) && <ProtectedAdminRoute
                                                component={ApiConnections}
                                                path='/admin/apiConnections'
                                            />}
                                            {userRole && validateUserRole('Alerts', userRole) && <ProtectedAdminRoute
                                                component={Alerts}
                                                path='/admin/alerts'
                                            />}
                                            {userRole && validateUserRole('ACG', userRole) && <ProtectedAdminRoute
                                                component={AcgUsersView}
                                                path='/admin/acg/:ACGId?'
                                            />}
                                            {userRole && validateUserRole('ClientMessages', userRole) && <ProtectedAdminRoute
                                                component={ClientMessages}
                                                path='/admin/messages'
                                            />}
                                            {userRole && validateUserRole('Collateral', userRole) && <ProtectedAdminRoute
                                                component={Collateral}
                                                path='/admin/collateral'
                                            />}
                                            <ProtectedAdminRoute
                                                component={() => (
                                                    <AdminLanding
                                                        connectedToCore={connectedToCore}
                                                        fetchedUserRoles={fetchedUserRoles}
                                                        user={user}
                                                        userRole={userRole}
                                                    />
                                                )}
                                                path='/admin'
                                            />
                                        </Switch>
                                    </Suspense>
                                </Admin>
                            );
                        }}
                    />
                    <ProtectedAdminRoute
                        component={() => <Redirect to='/admin' />}
                        path='/accounts'
                    />
                    <ProtectedAdminRoute
                        component={() => <Redirect to='/admin' />}
                        path='/login'
                    />
                    <Route
                        exact
                        path='/locked'
                        render={(props) => <LockedUser {...props} />}
                    />
                    <Route
                        exact
                        path='/offline'
                        render={(props) => <Offline {...props} />}
                    />
                    {authSSO ? <Route component={() => <Redirect to='/admin' />} /> : <Route component={() => <Redirect to='/admin_auth_login' />} />}
                </Switch>
            </>
        </Router>
    );
};

const AdminRoutes = () => (
    <Provider store={adminStore}>
        <AdminRouteList />
    </Provider>
);

export default AdminRoutes;